/**
 * Public Zoho Redirect — branded interstitial that auto-forwards to a
 * Zoho Books quotation or invoice URL. Mounted at:
 *   /connection/quote/:slug   (kind="quotation")
 *   /connection/invoice/:slug (kind="invoice")
 *
 * NO AUTH. The customer never sees the raw Zoho URL — they see a Pollen
 * Dots branded loading screen for ~1.5 s then land on Zoho. This gives
 * us a place to attach view-tracking later and protects brand trust.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { API } from '@/App';

const PublicZohoRedirect = ({ kind }) => {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | missing

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const path = kind === 'quotation' ? 'quote' : 'invoice';
        const res = await fetch(`${API}/public/${path}/${slug}`);
        if (!res.ok) { if (live) setStatus('missing'); return; }
        const data = await res.json();
        if (live) { setMeta(data); setStatus('ok'); }
      } catch (e) {
        if (live) setStatus('missing');
      }
    })();
    return () => { live = false; };
  }, [slug, kind]);

  // Hand off to Zoho 1.5s after meta lands — keeps brand on screen long
  // enough for the customer to register where the link came from.
  useEffect(() => {
    if (status !== 'ok' || !meta?.target_url) return;
    const t = setTimeout(() => {
      window.location.replace(meta.target_url);
    }, 1500);
    return () => clearTimeout(t);
  }, [status, meta]);

  if (status === 'missing') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#fdf6e6] text-[#3f2e3e] p-6 text-center">
        <div className="text-xl font-semibold mb-2">This link is no longer available.</div>
        <p className="text-sm text-[#6b5566] max-w-xs">
          If you were expecting it, please get in touch with your Pollen Dots travel curator.
        </p>
        <a href="https://www.pollendots.com" className="mt-6 text-[#b16e32] font-semibold border-b border-[#b16e3266] pb-0.5">
          Visit Pollen Dots →
        </a>
      </div>
    );
  }

  const label = kind === 'quotation' ? 'quotation' : 'invoice';
  const sub = kind === 'quotation'
    ? 'For your security we’re redirecting you to Zoho Books, our trusted billing partner.'
    : 'For your security we’re redirecting you to Zoho Books to view & pay securely.';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#fdf6e6] text-[#3f2e3e] p-6 text-center gap-4" data-testid={`public-${kind}-page`}>
      {/* Logo lockup — matches website header: wordmark on top, tagline
          directly beneath, both same width. */}
      <div className="flex flex-col items-center leading-tight">
        <img src="/og/pollen-logo.png" alt="Pollen Dots" className="h-10 sm:h-11 w-auto" data-testid={`public-${kind}-logo`} />
        <div className="text-[11px] sm:text-xs text-[#7a5a48] mt-1 tracking-wide ml-[44px] sm:ml-[50px]">Travel · Connect · Care</div>
      </div>
      <h1 className="text-lg font-semibold mt-2">Opening your {label}…</h1>
      <p className="text-sm text-[#6b5566] max-w-xs">{sub}</p>
      <div className="inline-flex items-center gap-2 text-xs text-[#7a5a48]">
        <Loader2 className="w-3.5 h-3.5 animate-spin text-[#b16e32]" />
        Redirecting in a moment
      </div>
      {meta?.target_url && (
        <a
          href={meta.target_url}
          className="mt-2 inline-flex items-center gap-2 bg-[#b16e32] text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow"
          data-testid={`public-${kind}-cta`}
        >
          Open {kind === 'quotation' ? 'Quotation' : 'Invoice'} now
        </a>
      )}
    </div>
  );
};

export default PublicZohoRedirect;
